/**
 * API de Comentários em Jobs/Tasks
 * Serviços para gerenciar comentários/anotações em Jobs/Tasks
 * 
 * @author Equipe GestaoeCRM
 * @date 07/10/2025
 * @version 0.22.0
 */

import api from './api';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// ============================================================================
// INTERFACES E TIPOS
// ============================================================================

/**
 * Interface de Usuário (simplificada)
 */
export interface CommentUser {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'GESTOR' | 'ANALISTA';
}

/**
 * Interface de Comentário
 */
export interface OSComment {
  id: string;
  orderOfServiceId: string;
  userId: string;
  comment: string;
  createdAt: string;
  updatedAt: string;
  user: CommentUser;
}

/**
 * Interface para criar comentário
 */
export interface CreateCommentData {
  comment: string;
}

/**
 * Interface para atualizar comentário
 */
export interface UpdateCommentData {
  comment: string;
}

/**
 * Interface de resposta de lista de comentários
 */
export interface CommentListResponse {
  success: boolean;
  data: OSComment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Interface de resposta padrão
 */
export interface CommentResponse {
  success: boolean;
  message: string;
  data: OSComment;
}

/**
 * Interface de resposta de delete
 */
export interface DeleteResponse {
  success: boolean;
  message: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Formata data para exibição relativa (ex: "há 2 horas", "há 3 dias")
 */
export const formatCommentDate = (dateString: string): string => {
  try {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: ptBR
    });
  } catch (error) {
    // console.error('Erro ao formatar data:', error);
    return dateString;
  }
};

/**
 * Formata data e hora completa para exibição
 */
export const formatCommentDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    // console.error('Erro ao formatar data/hora:', error);
    return dateString;
  }
};

/**
 * Verifica se o usuário atual pode editar o comentário
 */
export const canEditComment = (comment: OSComment, currentUserId: string, currentUserRole: string): boolean => {
  // ADMIN pode editar qualquer comentário
  if (currentUserRole === 'ADMIN') {
    return true;
  }
  
  // Autor pode editar seu próprio comentário
  return comment.userId === currentUserId;
};

/**
 * Verifica se o usuário atual pode deletar o comentário
 */
export const canDeleteComment = (comment: OSComment, currentUserId: string, currentUserRole: string): boolean => {
  // ADMIN pode deletar qualquer comentário
  if (currentUserRole === 'ADMIN') {
    return true;
  }
  
  // Autor pode deletar seu próprio comentário
  return comment.userId === currentUserId;
};

/**
 * Trunca comentário para preview
 */
export const truncateComment = (comment: string, maxLength: number = 100): string => {
  if (comment.length <= maxLength) {
    return comment;
  }
  return `${comment.substring(0, maxLength)}...`;
};

// ============================================================================
// API SERVICE
// ============================================================================

export const osCommentApi = {
  /**
   * Criar novo comentário em um Job/Task
   * 
   * @param osId - ID do Job/Task
   * @param data - Dados do comentário (comment)
   * @returns Promise com o comentário criado
   */
  createComment: async (osId: string, data: CreateCommentData): Promise<CommentResponse> => {
    try {
      // console.log('💬 osCommentApi.createComment - osId:', osId, 'data:', data);
      const response = await api.post(`/os/${osId}/comments`, data);
      // console.log('✅ Comentário criado:', response.data);
      return response.data;
    } catch (error: any) {
      // console.error('❌ Erro ao criar comentário:', error.response?.data || error);
      throw error;
    }
  },

  /**
   * Listar comentários de um Job/Task
   * 
   * @param osId - ID do Job/Task
   * @param page - Número da página (default: 1)
   * @param limit - Itens por página (default: 50)
   * @returns Promise com lista de comentários e paginação
   */
  listComments: async (osId: string, page: number = 1, limit: number = 50): Promise<CommentListResponse> => {
    try {
      // console.log('💬 osCommentApi.listComments - osId:', osId, 'page:', page, 'limit:', limit);
      const response = await api.get(`/os/${osId}/comments`, {
        params: { page, limit }
      });
      // console.log('✅ Comentários listados:', response.data.data.length, 'comentários');
      return response.data;
    } catch (error: any) {
      // console.error('❌ Erro ao listar comentários:', error.response?.data || error);
      throw error;
    }
  },

  /**
   * Obter detalhes de um comentário específico
   * 
   * @param osId - ID do Job/Task
   * @param commentId - ID do comentário
   * @returns Promise com os dados do comentário
   */
  getComment: async (osId: string, commentId: string): Promise<CommentResponse> => {
    try {
      // console.log('💬 osCommentApi.getComment - osId:', osId, 'commentId:', commentId);
      const response = await api.get(`/os/${osId}/comments/${commentId}`);
      // console.log('✅ Comentário obtido:', response.data);
      return response.data;
    } catch (error: any) {
      // console.error('❌ Erro ao obter comentário:', error.response?.data || error);
      throw error;
    }
  },

  /**
   * Atualizar comentário existente
   * 
   * @param osId - ID do Job/Task
   * @param commentId - ID do comentário
   * @param data - Dados atualizados (comment)
   * @returns Promise com o comentário atualizado
   */
  updateComment: async (osId: string, commentId: string, data: UpdateCommentData): Promise<CommentResponse> => {
    try {
      // console.log('💬 osCommentApi.updateComment - osId:', osId, 'commentId:', commentId, 'data:', data);
      const response = await api.put(`/os/${osId}/comments/${commentId}`, data);
      // console.log('✅ Comentário atualizado:', response.data);
      return response.data;
    } catch (error: any) {
      // console.error('❌ Erro ao atualizar comentário:', error.response?.data || error);
      throw error;
    }
  },

  /**
   * Deletar comentário
   * 
   * @param osId - ID do Job/Task
   * @param commentId - ID do comentário
   * @returns Promise com confirmação de exclusão
   */
  deleteComment: async (osId: string, commentId: string): Promise<DeleteResponse> => {
    try {
      // console.log('💬 osCommentApi.deleteComment - osId:', osId, 'commentId:', commentId);
      const response = await api.delete(`/os/${osId}/comments/${commentId}`);
      // console.log('✅ Comentário deletado:', response.data);
      return response.data;
    } catch (error: any) {
      // console.error('❌ Erro ao deletar comentário:', error.response?.data || error);
      throw error;
    }
  }
};

// ============================================================================
// REACT QUERY KEYS
// ============================================================================

/**
 * Query keys para React Query
 * Facilita invalidação de cache e gerenciamento de estado
 */
export const commentQueryKeys = {
  all: ['comments'] as const,
  lists: () => [...commentQueryKeys.all, 'list'] as const,
  list: (osId: string, page: number = 1, limit: number = 50) => [...commentQueryKeys.lists(), osId, page, limit] as const,
  details: () => [...commentQueryKeys.all, 'detail'] as const,
  detail: (osId: string, commentId: string) => [...commentQueryKeys.details(), osId, commentId] as const,
};

export default osCommentApi;

